import {
  Adherent,
  UserAdherents,
} from '../../../shared/interfaces/adherents/adherents.model';
import { AdherentService } from 'src/app/shared/services/adherent.service';
import { SelectionModel } from '@angular/cdk/collections';
import {
  AfterViewInit,
  Component,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort, MatSortHeader } from '@angular/material/sort';
import {
  MatTableDataSource,
  MatTable,
  MatColumnDef,
  MatHeaderCellDef,
  MatHeaderCell,
  MatCellDef,
  MatCell,
  MatHeaderRowDef,
  MatHeaderRow,
  MatRowDef,
  MatRow,
  MatNoDataRow,
} from '@angular/material/table';
import {
  catchError,
  concat,
  EMPTY,
  of,
  Subject,
  takeUntil,
  tap,
  throwError,
} from 'rxjs';
import { LinkAdherentToUser } from 'src/app/shared/interfaces/adherents/adherents.model';
import { Router, RouterLink } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { UpperCasePipe } from '@angular/common';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatButton, MatIconButton } from '@angular/material/button';
import { FormsModule } from '@angular/forms';
import { MatInput } from '@angular/material/input';
import {
  MatFormField,
  MatPrefix,
  MatSuffix,
} from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatToolbar } from '@angular/material/toolbar';
import { UserKeycloak } from 'src/app/shared/interfaces/keycloak/userKeycloak';

@Component({
  selector: 'app-adherents-user',
  templateUrl: './adherents-user.component.html',
  styleUrl: './adherents-user.component.scss',
  standalone: true,
  imports: [
    MatToolbar,
    RouterLink,
    MatIcon,
    MatFormField,
    MatPrefix,
    MatInput,
    FormsModule,
    MatButton,
    MatIconButton,
    MatSuffix,
    MatTable,
    MatSort,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatSortHeader,
    MatCellDef,
    MatCell,
    MatCheckbox,
    MatHeaderRowDef,
    MatHeaderRow,
    MatRowDef,
    MatRow,
    MatNoDataRow,
    MatPaginator,
    UpperCasePipe,
  ],
})
export class AdherentsUserComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  private readonly _destroying$ = new Subject<void>();
  // Table
  @ViewChild(MatSort) sort!: MatSort;
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  dataSource = new MatTableDataSource<UserKeycloak>();
  displayedColumns: string[] = ['lastName', 'firstName', 'email', 'actions'];
  // Filter
  globalFilter = '';
  adherent!: Adherent;
  listAdherents?: UserKeycloak[];
  listUserAdherentsByAdherent?: UserAdherents[];
  adherentsUserSelected = new SelectionModel<UserKeycloak>(true, []);
  // GPO context
  activiteAdherent: string = 'standard';
  isTitulaire: boolean = false;
  isSaving: boolean = false;
  saveError: string | null = null;
  // Router data
  undefined = 'undefined';
  routerData: any;

  constructor(
    private readonly router: Router,
    private readonly adherentService: AdherentService,
  ) {
    this.routerData = this.router.getCurrentNavigation()?.extras.state;
  }

  ngOnInit(): void {
    this.onTabSelected(1);
    if (typeof this.routerData === this.undefined) {
      this.router.navigate(['/adherents/']);
    } else {
      this.adherent = this.routerData.data as Adherent;
      // Determine GPO context from adherent's activite field
      this.activiteAdherent =
        this.adherent.activite?.toLowerCase() === 'gpo' ? 'gpo' : 'standard';
      this.fetchAllAdherent();
      this.fetchUserAdherentsByAdherent(this.adherent.idAdherent);
    }
  }

  ngAfterViewInit() {
    this.dataSource.sort = this.sort;
    this.dataSource.paginator = this.paginator;
  }

  /**
   * Called when the component is destroyed. Unsubscribes from all subscriptions to prevent memory leaks.
   */
  ngOnDestroy() {
    this._destroying$.next();
    this._destroying$.complete();
  }

  /**
   * Retrieves the list of all B2C suppliers via the supplierService service.
   * The data is stored in the listLournisseursBC2 property.
   */
  private fetchAllAdherent() {
    this.adherentService
      .fetchAllUsersKeycloakAdherents()
      .pipe(
        takeUntil(this._destroying$),
        tap((data: UserKeycloak[]) => {
          this.listAdherents = data;
          this.manageDataSourceTable(this.listAdherents);
          this.selectAdherentsFromSameGroup();
        }),
      )
      .subscribe();
  }

  /**
   * Retrieves the list of all UserAdherents By idAdherent via the cockpitADService service.
   * The data is stored in the listUserAdherentByAdherent property.
   * Errors are caught and returned as an Observable with throwError().
   */
  private fetchUserAdherentsByAdherent(idAdherent: number) {
    this.adherentService
      .fetchUserAdherentsByAdherent(idAdherent)
      .pipe(
        takeUntil(this._destroying$),
        tap((data: UserAdherents[]) => {
          this.listUserAdherentsByAdherent = data;
          this.selectAdherentsFromSameGroup();
        }),
        catchError((httpErrorResponse: HttpErrorResponse) => {
          if (httpErrorResponse.status === 404) {
            this.listUserAdherentsByAdherent = [];
            return of([]);
          } else {
            const error = httpErrorResponse;
            return throwError(() => error);
          }
        }),
      )
      .subscribe();
  }

  /**
   * Saves the selected rows as userAdherents for the current adherent.
   * Transforms the selected rows to Adherents format and sends the data to the server.
   * Uses sequential operations: delete first, then save, to avoid race conditions.
   */
  onSave() {
    if (this.isSaving) return;

    this.isSaving = true;
    this.saveError = null;

    const selectedRows: UserKeycloak[] = this.adherentsUserSelected.selected;
    const oldData: UserAdherents[] = this.listUserAdherentsByAdherent ?? [];
    const newToSave: UserAdherents[] =
      this.transformAdherentsToUserAdherents(selectedRows);

    const { additions, deletions } = this.getAdditionsAndDeletions(
      oldData,
      newToSave,
    );

    // Create observables for delete and save operations
    const deleteOp$ =
      deletions.length > 0 ? this.createDeleteOperation(deletions) : EMPTY;

    const saveOp$ =
      additions.length > 0 ? this.createSaveOperation(additions) : EMPTY;

    // Execute sequentially: delete first, then save
    concat(deleteOp$, saveOp$)
      .pipe(takeUntil(this._destroying$))
      .subscribe({
        complete: () => {
          this.isSaving = false;
          // Refresh data after successful save
          this.fetchUserAdherentsByAdherent(this.adherent.idAdherent);
        },
        error: (error: HttpErrorResponse) => {
          this.isSaving = false;
          this.saveError =
            error.message || "Une erreur est survenue lors de l'enregistrement";
        },
      });
  }

  /**
   * Creates an observable for the delete operation with error handling
   */
  private createDeleteOperation(deletions: UserAdherents[]) {
    const dataToDelete: LinkAdherentToUser[] =
      this.transformUserAdherentsToLinkAdherentToUser(deletions);
    return this.adherentService.deleteAdherentsToUser(dataToDelete).pipe(
      catchError((error: HttpErrorResponse) => {
        return throwError(() => error);
      }),
    );
  }

  /**
   * Creates an observable for the save operation with error handling
   */
  private createSaveOperation(additions: UserAdherents[]) {
    const dataToSave: LinkAdherentToUser[] =
      this.transformUserAdherentsToLinkAdherentToUser(additions);
    return this.adherentService
      .linkAdherentToUser(dataToSave, this.activiteAdherent, this.isTitulaire)
      .pipe(
        catchError((error: HttpErrorResponse) => {
          return throwError(() => error);
        }),
      );
  }

  /**
   * Returns an object with two arrays, containing the additions and deletions between the provided old and new objects.
   * @param {UserAdherents[]} oldObjects - The array of old objects.
   * @param {UserAdherents[]} newObjects - The array of new objects.
   * @returns {{ additions: UserAdherents[]; deletions: UserAdherents[] }} - An object with two properties:
   * "additions" containing the new objects that were not present in the oldObjects array,
   * and "deletions" containing the old objects that were not present in the newObjects array.
   */
  getAdditionsAndDeletions(
    oldObjects: UserAdherents[],
    newObjects: UserAdherents[],
  ): { additions: UserAdherents[]; deletions: UserAdherents[] } {
    const additions: UserAdherents[] = [];
    const deletions: UserAdherents[] = [];
    for (const oldObject of oldObjects) {
      if (
        !newObjects.some(
          (newObject) =>
            oldObject.idUser === newObject.idUser &&
            oldObject.idAdherent === newObject.idAdherent,
        )
      ) {
        deletions.push(oldObject);
      }
    }
    for (const newObject of newObjects) {
      if (
        !oldObjects.some(
          (oldObject) =>
            newObject.idUser === oldObject.idUser &&
            newObject.idAdherent === oldObject.idAdherent,
        )
      ) {
        additions.push(newObject);
      }
    }
    return { additions, deletions };
  }

  /**
   * Sets the titulaire flag for the save operation
   */
  setTitulaire(value: boolean) {
    this.isTitulaire = value;
  }

  /**
   * Transforms an array of "Adherents" objects by mapping each object to a new UserAdherents object
   * @param data An array of "Adherents" objects to transform
   * @returns An array of transformed objects with the specified properties
   */
  private transformAdherentsToUserAdherents(
    data: UserKeycloak[],
  ): UserAdherents[] {
    return data.map((adherent) => ({
      id: this.adherent.idAdherent,
      idUser: adherent.id,
      adresseMail: adherent.email || '',
      groupUser: 0,
      idAdherent: this.adherent.idAdherent,
    }));
  }

  /**
   * Transforms an array of "UserAdherents" objects by mapping each object to a new LinkAdherentToB2C object
   * @param data An array of "UserAdherents" objects to transform
   * @returns An array of transformed objects with the specified properties
   */
  private transformUserAdherentsToLinkAdherentToUser(
    data: UserAdherents[],
  ): LinkAdherentToUser[] {
    return data.map((userAdherent: UserAdherents) => ({
      idUser: userAdherent.idUser,
      idAdherent: userAdherent.idAdherent,
      adresseMail: userAdherent.adresseMail || '',
      groupUser: userAdherent.groupUser,
    }));
  }

  /**
   * Sets the array of data as the source for a data table called "dataSource".
   * @param data An array of data to set as the source for the data table.
   */
  private manageDataSourceTable(data: UserKeycloak[]) {
    if (data) {
      this.dataSource.data = data;
    }
  }

  /**
   * Handles the selection of an element by toggling its state.
   * @param element The element to select/deselect.
   */
  selectHandler(element: UserKeycloak) {
    this.adherentsUserSelected.toggle(element);
  }

  /**
   * Selects the Adherents suppliers associated with users from the same group as the current user.
   * The selected suppliers will be stored in the adherentsSelected property.
   * Uses idUser for matching to ensure consistency with getAdditionsAndDeletions().
   * @returns void
   */
  private selectAdherentsFromSameGroup() {
    if (this.listUserAdherentsByAdherent && this.listAdherents) {
      // Clear existing selection before re-selecting
      this.adherentsUserSelected.clear();

      for (const user of this.listAdherents) {
        const adherent = this.listUserAdherentsByAdherent.find(
          (a) => a.idUser === user.id,
        );
        if (adherent) {
          this.adherentsUserSelected.select(user);
        }
      }
    }
  }

  /**
   * Manages the filters to apply for the search bar
   * If the table has pagination then the pagination is placed on the first page
   * @param filter The value of the applied filter
   */
  applyFilter(filter: string) {
    this.globalFilter = filter;
    this.dataSource.filter = this.globalFilter;

    if (this.dataSource.paginator) {
      this.dataSource.paginator.firstPage();
    }
  }

  /**
   * Highlights a specific menu element by adding a CSS class.
   * @param {number} index - The index of the menu element to highlight.
   */
  onTabSelected(index: number): void {
    const menuElements = document.querySelectorAll('.menu-element');
    if (menuElements.length > 3 && index >= 0 && index < menuElements.length) {
      for (let i = 0; i < menuElements.length; i++) {
        const menuElement = menuElements[i] as HTMLElement;
        if (i === index) {
          menuElement.classList.remove('menu-element--default');
          menuElement.classList.add('menu-element--highlighted');
        } else {
          menuElement.classList.remove('menu-element--highlighted');
          menuElement.classList.add('menu-element--default');
        }
      }
    }
  }
}
